import { ReduxState } from "ra-core";
import { FC, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Stack, Box, Input, Divider, CircularProgress, Button, MenuItem, MenuList, Popover } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from '@/icons/Search';
import PopAutocompleteFilter from "../input/PopAutocompleteFilter";
import { osfCatalogRes, pfsCatalogRes, useFileCataSearchContext } from "./FileCataSearchContextProvider";
import { useFileCataAmbientDataContext } from "./FileCataAmbientDataProvider";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import { DataOwnerGroupResponseDto, PrimaryClassification, SecondaryClassificationResponseDto } from "@/gen/api/governance";
import { FileInstanceOptions, FileTagResponseDto } from "@/gen/api/securefile";
import SearchCriteriaChips, { ChipItem } from "../input/SearchCriteriaChips";
import { SimpleOption } from "../governance/search/AmbientFacilitiesContext";


const FileCatalogSearchRegion: FC<{
  ownerGroup?:boolean;
}> = (
  ownerGroup,
) => {
  const loading = useSelector<ReduxState>(state => state.admin.loading > 0)
  const { classifications, tags, documentTypes, infoTypes, ownerGroups } = useFileCataAmbientDataContext()
  const fileInstances: SimpleOption[] = [
    { key: 0, text: "All" },
    { key: 1, text: "Single" },
    { key: 2, text: "Multiple" }
  ]
  const { 
    filters, 
    currRes,
    searchName, 
    addSecondClassification, 
    removeSecondClassification, 
    addPrimaryClassification,
    removePrimaryClassification, 
    addSearchTag, 
    removeSearchTag, 
    addDocumentType,
    removeDocumentType,
    addInfoType,
    removeInfoType,
    addOwnerGroup,
    removeOwnerGroup,
    removeFileInstanceOption,
  } = useFileCataSearchContext()

  const [states, setStates] = useState({
    inputFocused: false,
    openResSwitch: false,
    openFileInstanceSwitch: false,
  })
  
  const criteriaChips = useMemo(() => {
    const arr = [] as ChipItem[][]
    if (currRes === osfCatalogRes || currRes === pfsCatalogRes) {
      arr.push([{
        id: '_res',
        label: 'Files',
        content: currRes === osfCatalogRes ? 'Encrypted' : 'Unencrypted',
        delete: () => setStates(v => ({...v, openResSwitch: true})),
      }])
    }
    if (filters.name) {
      arr.push([{
        id: 'name',
        label: 'Name',
        content: `like *${filters.name}*`,
        delete: () => searchName(null),
      }])
    }
    if (filters.primaryClassification.length > 0 || filters.secondClassification.length > 0) {
      arr.push([
        ...filters.primaryClassification.map(p => ({
          id: `${p}`,
          label: 'Classification',
          content: p == null ? 'Not Classified' : PrimaryClassification[p],
          delete: () => removePrimaryClassification(p),
        })),
        ...filters.secondClassification.map(s => ({
          id: s.secondaryClassificationId,
          label: 'Classification',
          content: (s as any).text,
          delete: () => removeSecondClassification(s),
        }))
      ])
    }
    if (filters.tags.length > 0) {
      arr.push(filters.tags.map(t => ({
        id: `t${t.tagId}`,
        label: 'Tag',
        content: t.name!,
        delete: () => removeSearchTag(t)
      })))
    }
    if (filters.documentTypes.length > 0) {
      arr.push(filters.documentTypes.map(dt => ({
        id: `dt_${dt}`,
        label: 'Document Type',
        content: dt,
        delete: () => removeDocumentType(dt)
      })))
    }
    if (filters.infoTypes.length > 0) {
      arr.push(filters.infoTypes.map(it => ({
        id: `it_${it}`,
        label: 'Info Type',
        content: it,
        delete: () => removeInfoType(it)
      })))
    }
    if (currRes === pfsCatalogRes) {
      arr.push([{
        id: 'fileInstances',
        label: 'File Instances',
        content: FileInstanceOptions[filters.fileInstanceOptions],
        delete: () => setStates(v => ({...v, openFileInstanceSwitch: true})),
      }])
    }
    if (filters.ownerGroup.length > 0) {
      arr.push(filters.ownerGroup.map(og => ({
        id: `it_${og.ownerGroupId}`,
        label: 'Owner Group',
        content: og.name!,
        delete: () => removeOwnerGroup(og)
      })))
    }
    return arr
  }, [currRes, filters.documentTypes, filters.fileInstanceOptions, filters.infoTypes, filters.name, filters.ownerGroup, filters.primaryClassification, filters.secondClassification, filters.tags, removeDocumentType, removeInfoType, removeOwnerGroup, removePrimaryClassification, removeSearchTag, removeSecondClassification, searchName])

  return (
    <>
      <Stack direction="row" alignItems="center" sx={{ p: 2 }}>
        {loading ? 
          <CircularProgress size={20} /> :
          <SearchIcon fontSize="small" />}
        <Box sx={{ flexGrow: 1, ml: 3 }}>
          <Input fullWidth placeholder="Search by Name" disableUnderline={true} 
            value={states.inputFocused ? filters.name : ''}
            onChange={(e) => searchName(e.target.value)}
            onFocus={() => setStates(v => ({...v, inputFocused: true }))}
            onBlur={() => setStates(v => ({...v, inputFocused: false }))}
          />
        </Box>
      </Stack>
      <Divider />
      <SearchCriteriaChips criteriaChips={criteriaChips} />
      <Divider />
      <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ p: 1 }} spacing={2}>
        {(currRes === osfCatalogRes || currRes === pfsCatalogRes) &&
          <Box>
            <PopResSwitcher openMenu={states.openResSwitch} setOpenMenu={(x) => setStates(v => ({...v, openResSwitch: x}))} />
          </Box>}
        <Box>
          <PopAutocompleteFilter
            label="Classification"
            AutocompleteProps={{
              id: 'classification-picker',
              options: classifications,
              getOptionLabel: (option) => option.text,
              renderOption: (props, option, { inputValue }) => {
                const matches = match(option.text, inputValue, {insideWords:true});
                const parts = parse(option.text, matches);

                return (
                  <li {...props}>
                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', wordWrap: 'normal', whiteSpace: 'nowrap' }}>
                      {parts.map((part, index) => (
                        <span
                          key={index}
                          style={{
                            fontWeight: part.highlight ? 700 : 400,
                          }}
                        >
                          {part.text}
                        </span>
                      ))}
                    </Box>
                  </li>
                );
              },
              onChange: (event, option) => {
                if (option.key < 0) {
                  const pcname = PrimaryClassification[Number(-option.key)]
                  const pc = PrimaryClassification[pcname as keyof typeof PrimaryClassification] ?? null
                  addPrimaryClassification(pc)
                  return
                }
                const clsItem = option as SecondaryClassificationResponseDto
                if (clsItem) {
                  addSecondClassification(clsItem)
                }
              },
            }}
          />
        </Box>
        <Box>
          <PopAutocompleteFilter
            label="Tag"
            AutocompleteProps={{
              id: 'tag-picker',
              options: tags,
              getOptionLabel: (option) => option.text,
              renderOption: (props, option, { inputValue }) => {
                const matches = match(option.text, inputValue, {insideWords:true});
                const parts = parse(option.text, matches);

                return (
                  <li {...props}>
                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', wordWrap: 'normal', whiteSpace: 'nowrap' }}>
                      {parts.map((part, index) => (
                        <span
                          key={index}
                          style={{
                            fontWeight: part.highlight ? 700 : 400,
                          }}
                        >
                          {part.text}
                        </span>
                      ))}
                    </Box>
                  </li>
                );
              },
              onChange: (event, option) => {
                const tagItem = option as FileTagResponseDto
                if (tagItem) {
                  addSearchTag(tagItem)
                }
              },
            }}
          />
        </Box>
        <Box>
          <PopAutocompleteFilter
            label="Document Type"
            AutocompleteProps={{
              id: 'doc-type-picker',
              options: documentTypes,
              getOptionLabel: (option) => option.text,
              renderOption: (props, option, { inputValue }) => {
                const matches = match(option.text, inputValue, {insideWords:true});
                const parts = parse(option.text, matches);

                return (
                  <li {...props}>
                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', wordWrap: 'normal', whiteSpace: 'nowrap' }}>
                      {parts.map((part, index) => (
                        <span
                          key={index}
                          style={{
                            fontWeight: part.highlight ? 700 : 400,
                          }}
                        >
                          {part.text}
                        </span>
                      ))}
                    </Box>
                  </li>
                );
              },
              onChange: (event, option) => {
                const docTypeItem = option as SimpleOption
                if (docTypeItem) {
                  addDocumentType(docTypeItem.text)
                }
              },
            }}
          />
        </Box>
        <Box>
          <PopAutocompleteFilter
            label="Info Type"
            AutocompleteProps={{
              id: 'info-type-picker',
              options: infoTypes,
              getOptionLabel: (option) => option.text,
              renderOption: (props, option, { inputValue }) => {
                const matches = match(option.text, inputValue, {insideWords:true});
                const parts = parse(option.text, matches);

                return (
                  <li {...props}>
                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', wordWrap: 'normal', whiteSpace: 'nowrap' }}>
                      {parts.map((part, index) => (
                        <span
                          key={index}
                          style={{
                            fontWeight: part.highlight ? 700 : 400,
                          }}
                        >
                          {part.text}
                        </span>
                      ))}
                    </Box>
                  </li>
                );
              },
              onChange: (event, option) => {
                const infoTypeItem = option as SimpleOption
                if (infoTypeItem) {
                  addInfoType(infoTypeItem.text)
                }
              },
            }}
          />
        </Box>
        {/* {currRes !== osfCatalogRes &&
          <Box>
            <PopAutocompleteFilter
              label="File Instances"
              AutocompleteProps={{
                id: 'file-instances-picker',
                options: fileInstances,
                getOptionLabel: (option) => option.text,
                renderOption: (props, option, { inputValue }) => {
                  const matches = match(option.text, inputValue, {insideWords:true});
                  const parts = parse(option.text, matches);

                  return (
                    <li {...props}>
                      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', wordWrap: 'normal', whiteSpace: 'nowrap' }}>
                        {parts.map((part, index) => (
                          <span
                            key={index}
                            style={{
                              fontWeight: part.highlight ? 700 : 400,
                            }}
                          >
                            {part.text}
                          </span>
                        ))}
                      </Box>
                    </li>
                  );
                },
                onChange: (event, option) => {
                  const fileInstancesItem = option as SimpleOption
                  if (fileInstancesItem) {
                    addFileInstanceOption(fileInstancesItem.text)
                  }
                },
              }}
            />
          </Box>
        } */}
        {currRes === pfsCatalogRes &&
          <Box>
            <PopFileInstanceSwitcher openMenu={states.openFileInstanceSwitch} setOpenMenu={(x) => setStates(v => ({...v, openFileInstanceSwitch: x}))} />
          </Box>}
        {ownerGroup &&
          <Box>
            <PopAutocompleteFilter
              label="Owner Group"
              AutocompleteProps={{
                id: 'owner-group-picker',
                options: ownerGroups,
                getOptionLabel: (option) => option.text,
                renderOption: (props, option, { inputValue }) => {
                  const matches = match(option.text, inputValue, {insideWords:true});
                  const parts = parse(option.text, matches);

                  return (
                    <li {...props}>
                      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', wordWrap: 'normal', whiteSpace: 'nowrap' }}>
                        {parts.map((part, index) => (
                          <span
                            key={index}
                            style={{
                              fontWeight: part.highlight ? 700 : 400,
                            }}
                          >
                            {part.text}
                          </span>
                        ))}
                      </Box>
                    </li>
                  );
                },
                onChange: (event, option) => {
                  const ownerGroupItem = option as DataOwnerGroupResponseDto
                  if (ownerGroupItem) {
                    addOwnerGroup(ownerGroupItem)
                  }
                },
              }}
            />
          </Box>}
      </Stack>
    </>
  )
}

export default FileCatalogSearchRegion;


const PopResSwitcher: FC<{openMenu: boolean, setOpenMenu: (x:boolean)=>void}> = ({openMenu, setOpenMenu}) => {
  const { currRes, switchCurrRes } = useFileCataSearchContext()
  
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  return (
    <>
      <Button
        color="inherit"
        endIcon={<ExpandMoreIcon fontSize="small" />}
        onClick={() => setOpenMenu(true)}
        ref={anchorRef}
      >
        Encrypted / Unencrypted
      </Button>
      {openMenu &&
        <Popover
          anchorEl={anchorRef.current}
          open={openMenu}
          onClose={() => setOpenMenu(false)}
          PaperProps={{ sx: {width: 180 } }}
        >
          <MenuList id="split-button-menu" autoFocusItem>
            <MenuItem
              selected={osfCatalogRes === currRes}
              onClick={(event) => {
                if (osfCatalogRes !== currRes) {
                  switchCurrRes()
                }
                setOpenMenu(false)
              }}
            >
              Encrypted Files
            </MenuItem>
            <MenuItem
              selected={pfsCatalogRes === currRes}
              onClick={(event) => {
                if (pfsCatalogRes !== currRes) {
                  switchCurrRes()
                }
                setOpenMenu(false)
              }}
            >
              Unencrypted Files
            </MenuItem>
          </MenuList>
        </Popover>}
    </>
  )
}

const PopFileInstanceSwitcher: FC<{openMenu: boolean, setOpenMenu: (x:boolean)=>void}> = ({openMenu, setOpenMenu}) => {
  const { filters, addFileInstanceOption } = useFileCataSearchContext()
  
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  return (
    <>
      <Button
        color="inherit"
        endIcon={<ExpandMoreIcon fontSize="small" />}
        onClick={() => setOpenMenu(true)}
        ref={anchorRef}
      >
        File Instances
      </Button>
      {openMenu &&
        <Popover
          anchorEl={anchorRef.current}
          open={openMenu}
          onClose={() => setOpenMenu(false)}
          PaperProps={{ sx: {width: 180 } }}
        >
          <MenuList id="split-button-menu" autoFocusItem>
            <MenuItem
              selected={filters.fileInstanceOptions === FileInstanceOptions.All}
              onClick={(event) => {
                if (filters.fileInstanceOptions !== FileInstanceOptions.All) {
                  addFileInstanceOption(FileInstanceOptions.All)
                }
                setOpenMenu(false)
              }}
            >
              All
            </MenuItem>
            <MenuItem
              selected={filters.fileInstanceOptions === FileInstanceOptions.Single}
              onClick={(event) => {
                if (filters.fileInstanceOptions !== FileInstanceOptions.Single) {
                  addFileInstanceOption(FileInstanceOptions.Single)
                }
                setOpenMenu(false)
              }}
            >
              Single
            </MenuItem>
            <MenuItem
              selected={filters.fileInstanceOptions === FileInstanceOptions.Multiple}
              onClick={(event) => {
                if (filters.fileInstanceOptions !== FileInstanceOptions.Multiple) {
                  addFileInstanceOption(FileInstanceOptions.Multiple)
                }
                setOpenMenu(false)
              }}
            >
              Multiple
            </MenuItem>
          </MenuList>
        </Popover>}
    </>
  )
}
